import SelectComponent from '@/components/SelectComponent';
import styled from 'styled-components';
import MaterialIcon from '@/components/icon/MaterialIcon';
import theme from '@/styles/theme';
import { useEffect } from 'react';
import { preventArrowKeys } from '@/utils/form';
import useMedicineModalViewModel from '@/hooks/medicine/useMedicineModalViewModel';
import { ReactComponent as Close } from '@/assets/images/common/close.svg';
import Overlay from '@/components/Overlay';
import { toast } from 'react-toastify';
import ValidationCheckCircle from '@/components/icon/ValidationCheckCircle';
import MedicineDeleteModal from './MedicineDeleteModal';

interface MedicineModalProps {
  isMedicineModalOpen: boolean;
  onCloseModal: () => void;
  editingMedicineId?: string;
}

const MedicineModal = ({
  isMedicineModalOpen,
  onCloseModal,
  editingMedicineId
}: MedicineModalProps) => {
  const viewModel = useMedicineModalViewModel({
    isOpen: isMedicineModalOpen,
    onCloseModal,
    editingMedicineId
  });

  const onOpenToast = (message: string) => {
    toast.info(
      <ToastWrapper>
        <ValidationCheckCircle />
        {message}
      </ToastWrapper>,
      {
        position: 'top-center',
        autoClose: 3000,
        hideProgressBar: true,
        closeOnClick: true,
        draggable: true,
        closeButton: false,
        icon: false,
        onClose: viewModel.onClearToastMessage
      }
    );
  };

  useEffect(() => {
    viewModel.toastMessage && onOpenToast(viewModel.toastMessage);
  }, [viewModel.toastMessage]);

  return (
    <Overlay>
      <ModalWrapper>
        <Header>
          <HeaderTitle>{`약품 ${
            !!editingMedicineId ? '수정' : '추가'
          }`}</HeaderTitle>
          <Close
            style={{
              cursor: 'pointer',
              width: 40,
              height: 40,
              padding: 9
            }}
            onClick={onCloseModal}
          />
        </Header>
        <Wrapper>
          <SelectWrapper>
            <SelectLabel>진료 증상</SelectLabel>
            <SelectComponent
              options={viewModel.medicineStructures.treatmentMedicineOptions}
              placeholder={'진료 증상을 선택해주세요.'}
              value={viewModel.selectedMedicine.treatmentOption}
              disabled={!!editingMedicineId}
              onChange={viewModel.onChangeTreatmentMedicineOption}
              selectStyle={{ width: '480px' }}
              isSearchable={false}
              useDefault
            />
          </SelectWrapper>
          <SelectWrapper>
            <SelectLabel>약품명</SelectLabel>
            <SelectComponent
              options={viewModel.pharmacyMedicineOptions}
              placeholder={'약품을 선택해주세요'}
              value={viewModel.selectedMedicine.medicineOption ?? ''}
              disabled={!!editingMedicineId}
              onChange={viewModel.onChangePharmacyMedicineOption}
              selectStyle={{ width: '480px' }}
              isSearchable={false}
              useDefault
            />
          </SelectWrapper>
          {viewModel.selectedMedicine.medicineOption && (
            <UnitWrapper>
              <UnitTitle>
                규격 및 금액 입력
                <AddUnitButton
                  onClick={viewModel.addUnitInput}
                  disabled={viewModel.isDisabledAddButton}>
                  <MaterialIcon
                    name={'add'}
                    size={20}
                    color={
                      viewModel.isDisabledAddButton
                        ? theme.DF_COLOR.WHITE
                        : theme.DF_COLOR.GREEN500
                    }
                  />{' '}
                  추가
                </AddUnitButton>
              </UnitTitle>
              <UnitSelectWrapper>
                {viewModel.selectedMedicine.medicineStandardPrices?.map(
                  (_, index) => (
                    <UnitSelectRow key={`medicine_row_${index}`}>
                      <SelectWrapper>
                        <SelectLabel>규격</SelectLabel>
                        <SelectComponent
                          options={viewModel.pharmacyMedicineStandardOptions}
                          value={
                            viewModel.selectedMedicine.medicineStandardPrices[
                              index
                            ].option
                          }
                          disabled={false}
                          onChange={value =>
                            viewModel.onChangeMedicineStandardOption(
                              value,
                              index
                            )
                          }
                          selectStyle={{ width: '200px' }}
                          isSearchable={false}
                          useDefault
                        />
                      </SelectWrapper>
                      <SelectWrapper>
                        <SelectLabel>금액</SelectLabel>
                        <PriceInputWrapper>
                          <PriceInput
                            error={viewModel.priceErrors?.errorField[index]}
                            placeholder={'숫자 입력'}
                            step={10}
                            value={
                              viewModel.selectedMedicine.medicineStandardPrices[
                                index
                              ].price
                            }
                            onChange={e =>
                              viewModel.onChangePrice(
                                Number(e.target.value),
                                index
                              )
                            }
                            onBlur={viewModel.onCheckPriceError}
                            onKeyDown={e => preventArrowKeys(e)}
                            onWheel={event => event.currentTarget.blur()}
                          />
                          <PriceUnit>원</PriceUnit>
                        </PriceInputWrapper>
                      </SelectWrapper>
                      <UnitDeleteButton
                        onClick={() => viewModel.onDeletePrice(index)}>
                        삭제
                      </UnitDeleteButton>
                    </UnitSelectRow>
                  )
                )}
              </UnitSelectWrapper>
            </UnitWrapper>
          )}
          <Bottom>
            {viewModel.priceErrors?.errorField.some(price => price) && (
              <Error>{viewModel.priceErrors.errorMessage}</Error>
            )}
            <SubmitButton
              disabled={viewModel.isDisableSubmit}
              onClick={viewModel.onSubmit}>
              {!!editingMedicineId ? '수정' : '완료'}
            </SubmitButton>
          </Bottom>
        </Wrapper>
      </ModalWrapper>
      {viewModel.isMedicineDeleteModalOpen && (
        <MedicineDeleteModal
          medicineName={viewModel.selectedMedicine.medicineOption.label}
          onCloseModal={viewModel.onChangeMedicineDeleteModalStatus}
          onSubmit={viewModel.onUpsertPharmacyHandlingMedicines}
        />
      )}
    </Overlay>
  );
};

export default MedicineModal;

const ModalWrapper = styled.div`
  background-color: ${theme.DF_COLOR.WHITE};
  border-radius: 5px;
  padding: 32px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 0;
`;

const HeaderTitle = styled.div`
  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI24B};
    color: ${theme.DF_COLOR.GRAY700};
  `};
`;

const Wrapper = styled.div`
  padding-top: 40px;
`;

const UnitSelectRow = styled.div`
  display: flex;
  align-items: center;

  & > div {
    margin-right: 12px;
  }
`;

const SelectWrapper = styled.div`
  margin-bottom: 20px;
`;

const SelectLabel = styled.div`
  margin-bottom: 8px;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI16R};
    color: ${theme.DF_COLOR.GRAY600};
    `};
`;

const UnitWrapper = styled.div`
  margin-top: 40px;
`;

const UnitTitle = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI17M};
    color: ${theme.DF_COLOR.GRAY600};
  `};
`;

const UnitSelectWrapper = styled.div`
  max-height: 380px;
  overflow: hidden;
  overflow-y: scroll;
`;

const AddUnitButton = styled.button`
  display: flex;
  align-items: center;
  border-radius: 4px;
  padding: 6px 16px;
  margin-bottom: 12px;

  .material_icon {
    margin-right: 8px;
  }
  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI14SB};;
    color: ${theme.DF_COLOR.GREEN500};
    border: 1px solid ${theme.DF_COLOR.GREEN500};
    background: ${theme.DF_COLOR.WHITE};

    &:hover:enabled {
      cursor: pointer;
      background: ${theme.DF_COLOR.GREEN100};

    };

    &:active:enabled {
      background: ${theme.DF_COLOR.GREEN200};
    };

    &:disabled {
      color: ${theme.DF_COLOR.WHITE};
      background: ${theme.DF_COLOR.GRAY100};
      border-color: ${theme.DF_COLOR.GRAY100};
    };
  `};
`;

const UnitDeleteButton = styled.div`
  border-radius: 8px;
  padding: 10px 16px;
  margin-top: 12px;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI14SB};
    color: ${theme.DF_COLOR.RED700};
    background: ${theme.DF_COLOR.RED100};

    &:hover {
      cursor: pointer;
      opacity: 0.6;
    }

    &:active {
      opacity: 1;
    }
  `};
`;

const PriceInputWrapper = styled.div`
  position: relative;
`;

const PriceInput = styled.input<{ error?: boolean }>`
  width: 200px;
  height: 42px;
  padding: 0 32px 0 16px;
  border-radius: 4px;

  ${({ theme, error }) => `
    ${theme.DF_TYPOGRAPHY.UI17R};
    color: ${theme.DF_COLOR.GRAY900};
    border: 1px solid ${
      !error ? theme.DF_COLOR.GRAY100 : theme.DF_COLOR.RED700
    };
  `};
`;

const PriceUnit = styled.span`
  position: absolute;
  right: 16px;
  bottom: 10px;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI17R};
    color: ${theme.DF_COLOR.GRAY500};
  `};
`;

const Bottom = styled.div`
  width: 100%;
  margin-top: 20px;
`;

const Error = styled.div`
  margin-bottom: 12px;
  text-align: center;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI14M};
    color: ${theme.DF_COLOR.RED900};
  `};
`;
const SubmitButton = styled.button`
  width: 100%;
  border-radius: 4px;
  padding: 12px 0;

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI16SB};
    color: ${theme.DF_COLOR.WHITE};
    background: ${theme.DF_COLOR.GREEN600};

    &:hover {
      cursor: pointer;
      background: ${theme.DF_COLOR.GREEN500};
    }

    &:active {
      background: ${theme.DF_COLOR.GREEN700};
    }

    &:disabled {
      background: ${theme.DF_COLOR.GRAY100};
    }
  `};
`;

const ToastWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;

  svg {
    margin-right: 12px;
  }

  ${({ theme }) => `
    ${theme.DF_TYPOGRAPHY.UI16SB};
    color: ${theme.DF_COLOR.WHITE};
  `}
`;
